import { inviteDestination, refreshSocialSummary } from "../net/social-actions.js";
import { notificationSummary } from "../net/notification-summary.js";
import { useEffect, useState } from "react";
import { Bell, CheckCheck, Eye, Radio, RefreshCw, Send, UserPlus, WifiOff } from "lucide-react";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "../app/navigation.js";
import { Avatar } from "../art/Avatar.js";
import {
  api,
  ApiError,
  type LiveTable,
  type PublicPlayer,
  type UserEvent,
} from "../net/api.js";
import { usePlayerState } from "../app/ChannelProvider.js";
import { pendingInvites, playerStore } from "../net/playerStore.js";
import { mobileCopy } from "../app/mobileCopy.js";
import { useLocale } from "../i18n.js";
import {
  Button,
  EmptyState,
  HeaderAction,
  PageHeader,
  Panel,
  Skeleton,
} from "../ui/primitives.js";
import "./mobile-screens.css";

export function LiveWatchScreen() {
  const { locale } = useLocale();
  const c = (key: Parameters<typeof mobileCopy>[1]) => mobileCopy(locale, key);
  const navigate = useNavigate();
  const [tables, setTables] = useState<LiveTable[] | null>(null);
  const [failed, setFailed] = useState(false);
  const load = () => {
    setFailed(false);
    void api.liveTables().then((result) => setTables(result.tables)).catch(() => setFailed(true));
  };
  useEffect(load, []);
  return (
    <div className="screen-stack detail-screen">
      <PageHeader title={c("publicWatch")} backTo="/" />
      {tables === null && !failed ? (
        <Skeleton lines={6} />
      ) : failed ? (
        <EmptyState
          title={c("watchLoadError")}
          icon={<WifiOff />}
          action={<Button onClick={load}><RefreshCw size={16} aria-hidden="true" />{c("retry")}</Button>}
        />
      ) : tables?.length === 0 ? (
        <EmptyState
          title={c("noLive")}
          hint={c("noLiveHint")}
          icon={<Radio />}
          action={<Button onClick={load}><RefreshCw size={16} aria-hidden="true" />{c("refresh")}</Button>}
        />
      ) : (
        <div className="watch-list">
          {tables?.map((table) => (
            <Panel className="watch-card" key={table.roomId}>
              <div>
                <small>
                  {table.mode === "ranked" ? c("ranked") : c("gold")}
                </small>
                <h2>
                  {table.rulesetId === "standard"
                    ? c("rulesStandard")
                    : table.rulesetId}
                </h2>
                <p>
                  {table.mode === "gold" ? `${table.coinTier} · ` : ""}
                  {c("inProgress")}
                </p>
              </div>
              <Button
                variant="primary"
                onClick={() => navigate(`/watch/${table.roomId}`)}
              >
                {c("watch")}
              </Button>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}

export function PlayerScreen() {
  const { locale } = useLocale();
  const c = (key: Parameters<typeof mobileCopy>[1]) => mobileCopy(locale, key);
  const { userId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo =
    (location.state as { from?: string } | null)?.from ?? "/social";
  const [player, setPlayer] = useState<PublicPlayer | null>(null);
  const [action, setAction] = useState<"follow" | "invite" | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    void api.player(userId).then(setPlayer).catch(() => setFailed(true));
  }, [userId]);
  if (failed) return (
    <div className="screen-stack detail-screen">
      <PageHeader title={c("playerHome")} backTo={backTo} />
      <EmptyState title={c("couldNotLoad")} icon={<WifiOff />} action={<Button onClick={() => window.location.reload()}><RefreshCw size={16} aria-hidden="true" />{c("retry")}</Button>} />
    </div>
  );
  if (!player) return <Skeleton lines={7} />;
  const viewedPlayer = player;

  async function follow() {
    if (viewedPlayer.following) return;
    setAction("follow"); setNote(null);
    try {
      await api.follow(viewedPlayer.userId);
      setPlayer({ ...viewedPlayer, following: true });
    } catch {
      setNote(c("inviteFailed"));
    } finally { setAction(null); }
  }

  async function inviteToParty() {
    setAction("invite"); setNote(null);
    try {
      const party = await inviteDestination("duo");
      const result = await api.invite(viewedPlayer.userId, "duo", party.code, crypto.randomUUID());
      playerStore.set((state) => ({ ...state, sentInvitesById: { ...state.sentInvitesById, [result.invite.id]: result.invite } }));
      setNote(c("inviteSent"));
    } catch {
      setNote(c("inviteFailed"));
    } finally { setAction(null); }
  }
  return (
    <div className="screen-stack detail-screen">
      <PageHeader title={c("playerHome")} backTo={backTo} />
      <Panel className="public-player">
        <Avatar avatarId={player.avatarId} size={88} />
        <h2>{player.name}</h2>
        <p>
          Lv.{player.level} · {player.rating} {c("ratingSuffix")}
        </p>
        <div className="metric-row">
          <span>
            <b>{Math.round(player.winRate * 100)}%</b>
            <small>{c("publicWinRate")}</small>
          </span>
          <span>
            <b>{player.matches}</b>
            <small>{c("publicMatches")}</small>
          </span>
        </div>
      </Panel>
      <div className="action-pair">
        {player.canFollow && (
          <Button
            variant={player.following ? "secondary" : "primary"}
            busy={action === "follow"}
            disabled={player.following}
            onClick={() => void follow()}
          >
            <UserPlus size={16} aria-hidden="true" />
            {player.following ? c("following") : c("follow")}
          </Button>
        )}
        {player.canInvite && <Button busy={action === "invite"} onClick={() => void inviteToParty()}><Send size={16} aria-hidden="true" />{c("invitedPlay")}</Button>}
        {player.canWatch && (
          <Button variant="secondary" onClick={() => navigate("/watch")}><Eye size={16} aria-hidden="true" />{c("watchTable")}</Button>
        )}
      </div>
      {note !== null && <p className="success-note" role="status">{note}</p>}

    </div>
  );
}

export function NotificationsScreen() {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const c = (key: Parameters<typeof mobileCopy>[1]) => mobileCopy(locale, key);
  const [items, setItems] = useState<UserEvent[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  /**
   * Live invitations come from the channel store, not from this list.
   *
   * The list is history — what has happened, and whether it has been read. An
   * invitation is a thing you can still act on, and it has a state machine
   * behind it, so it is rendered from the same store the shell's badge reads.
   * That is what keeps accepting here and the badge up there from disagreeing.
   */
  const invites = usePlayerState((state) => pendingInvites(state));

  // An unreachable server leaves an empty list rather than a permanent
  // skeleton, and never an unhandled rejection.
  const load = () => {
    setFailed(false);
    void api.events().then((result) => setItems(result.events)).catch(() => setFailed(true));
  };
  useEffect(load, []);

  /**
   * Accept, then go where the server sends us.
   *
   * Never a link built from the invitation's own payload: that is how a player
   * ended up walking into a lobby that had filled up or expired, with the
   * server never learning the invitation had been taken.
   */
  async function accept(inviteId: string) {
    setBusy(inviteId);
    try {
      const result = await api.acceptInvite(inviteId);
      void refreshSocialSummary().catch(() => undefined);
      navigate(result.destination.type === "party"
        ? `/party/${result.destination.id}`
        : `/room/${result.destination.code}`);
    } catch (error) {
      const code = error instanceof ApiError ? error.code : "";
      setNote(code === "invite_expired" ? c("expiredInvite") : c("inviteFailed"));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="screen-stack detail-screen">
      <PageHeader
        title={c("notificationsTitle")}
        backTo="/"
        action={
          <HeaderAction
            label={c("markAllRead")}
            icon={<CheckCheck />}
            onClick={() => void api.readAllEvents().then(refreshSocialSummary).then(load).catch(() => setNote(c("couldNotLoad")))}
          />
        }
      />
      {note !== null && <p className="success-note" role="status">{note}</p>}

      {invites.length > 0 && (
        <div className="notification-list">
          {invites.map((invite) => (
            <Panel key={invite.id} className="notification-item unread">
              <div>
                <b>{c(invite.destinationType === "party" ? "inviteParty" : "inviteLobby")}</b>
                <p>{notificationSummary({ type: "invite.resolved", payload: invite }, locale)}</p>
                <small>{new Date(invite.createdAt).toLocaleString(locale)}</small>
              </div>
              <div className="invite-actions">
                <Button size="sm" busy={busy === invite.id} onClick={() => void accept(invite.id)}>
                  {c("acceptInvite")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void api.declineInvite(invite.id).then(refreshSocialSummary).catch(() => setNote(c("inviteFailed")))}
                >
                  {c("declineInvite")}
                </Button>
              </div>
            </Panel>
          ))}
        </div>
      )}

      {failed ? (
        <EmptyState title={c("couldNotLoad")} icon={<WifiOff />} action={<Button onClick={load}><RefreshCw size={16} aria-hidden="true" />{c("retry")}</Button>} />
      ) : items === null ? (
        <Skeleton lines={6} />
      ) : items.length === 0 && invites.length === 0 ? (
        <EmptyState
          title={c("noNotifications")}
          hint={c("notificationsHint")}
          icon={<Bell />}
        />
      ) : (
        <div className="notification-list">
          {items.map((item) => (
            <Panel
              key={item.id}
              className={`notification-item${item.readAt === null ? " unread" : ""}`}
            >
              <div>
                <b>{item.type.startsWith("invite.") && item.payload && typeof item.payload === "object" && "destinationType" in item.payload
                  ? c(item.payload.destinationType === "party" ? "inviteParty" : "inviteLobby")
                  : notificationTitle(item.type, c)}</b>
                <p>
                  {notificationSummary(item, locale)}
                </p>
                <small>{new Date(item.createdAt).toLocaleString(locale)}</small>
              </div>
              {item.readAt === null && (
                <Button
                  size="sm"
                  onClick={() => void api.readEvent(item.id).then(refreshSocialSummary).then(load).catch(() => setNote(c("couldNotLoad")))}
                >
                  {c("markRead")}
                </Button>
              )}
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A human label for one channel event type.
 *
 * The types are the closed union in the protocol package, so an unhandled one
 * falls back rather than rendering a raw identifier.
 */
function notificationTitle(
  type: string,
  c: (key: Parameters<typeof mobileCopy>[1]) => string,
) {
  const titles: Record<string, Parameters<typeof mobileCopy>[1]> = {
    "invite.created": "inviteLobby",
    "invite.resolved": "inviteLobby",
    "friend.followed": "newFollow",
    "room.ready": "friendTable",
    "reward.granted": "notificationGame",
  };
  const key = titles[type];
  return key === undefined ? c("notificationGame") : c(key);
}
